import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import DiamondOutlined from '@mui/icons-material/DiamondOutlined';
import PlayArrowOutlined from '@mui/icons-material/PlayArrowOutlined';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { IconBadge } from '../../IconBadge.js';
import { iconFor } from '../../workflowIcons.js';
import type { ChainCard } from '../data/alisonRun.js';

const PAPER = {
  bgcolor: 'background.paper',
  borderRadius: 1,
  boxShadow: 1,
  p: 2,
  border: '2px solid',
  borderColor: 'background.paper',
} as const;

function Line({ width }: { width: string }): ReactNode {
  return <Box sx={{ height: 5, width, bgcolor: 'primary.main', opacity: 0.25, borderRadius: 1 }} />;
}

// An illustration of what the action delivers; an action without one shows its icon.
export function DestinationArtwork({ card }: { card: ChainCard }): ReactNode {
  const kind = card.workflowId;
  if (kind === 'getHiddenGems') {
    return (
      <Stack sx={{ ...PAPER, width: 126, gap: 1.5, transform: 'rotate(-5deg)' }}>
        <DiamondOutlined sx={{ fontSize: 28 }} />
        <Line width="85%" />
        <Line width="60%" />
        <Stack direction="row" sx={{ gap: 0.5, alignItems: 'flex-end', height: 28 }}>
          {[10, 16, 13, 25, 28].map((height, index) => (
            <Box
              key={height}
              sx={{
                flex: 1,
                height,
                bgcolor: 'primary.main',
                opacity: index === 4 ? 0.9 : 0.25,
                borderRadius: '2px 2px 0 0',
              }}
            />
          ))}
        </Stack>
      </Stack>
    );
  }
  if (kind === 'getCombinations') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 54px)',
          gap: 1,
          transform: 'rotate(-5deg)',
        }}
      >
        {[0, 1, 2, 3].map((tile) => (
          <Box
            key={tile}
            sx={{ ...PAPER, p: 1, height: 48, display: 'grid', alignItems: 'center' }}
          >
            <Line width="80%" />
          </Box>
        ))}
      </Box>
    );
  }
  if (kind === 'getComparison') {
    return (
      <Stack sx={{ ...PAPER, width: 140, gap: 1.5 }}>
        <Line width="65%" />
        {[
          ['75%', '45%'],
          ['50%', '85%'],
        ].map(([first, second]) => (
          <Stack key={first} sx={{ gap: 0.5 }}>
            <Box sx={{ width: first, height: 10, bgcolor: 'primary.main', borderRadius: 0.5 }} />
            <Box
              sx={{
                width: second,
                height: 10,
                bgcolor: 'primary.main',
                opacity: 0.25,
                borderRadius: 0.5,
              }}
            />
          </Stack>
        ))}
      </Stack>
    );
  }
  if (kind === 'generateIdeationBrief' || kind === 'createBrief') {
    return (
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        {(kind === 'createBrief' ? [-6, 6] : [5]).map((rotation, index) => (
          <Stack
            key={rotation}
            sx={{
              ...PAPER,
              width: kind === 'createBrief' ? 75 : 108,
              gap: 1.5,
              ml: index > 0 ? -2 : 0,
              transform: `rotate(${rotation}deg)`,
            }}
          >
            <Line width="90%" />
            <Line width="70%" />
            <Line width="85%" />
            <Line width="55%" />
          </Stack>
        ))}
      </Stack>
    );
  }
  if (kind === 'generateIterations') {
    return (
      <Stack sx={{ ...PAPER, width: 145, gap: 1.5, transform: 'rotate(-4deg)' }}>
        {['90%', '70%', '80%'].map((width) => (
          <Stack key={width} direction="row" sx={{ gap: 1, alignItems: 'center' }}>
            <CheckCircleOutlineOutlined sx={{ fontSize: 14 }} />
            <Line width={width} />
          </Stack>
        ))}
      </Stack>
    );
  }
  if (kind === 'generateStoryboard' || kind === 'generateBannerStoryboard') {
    return (
      <Stack sx={{ ...PAPER, width: 146, gap: 1, transform: 'rotate(-4deg)' }}>
        {[0, 1, 2].map((index) => (
          <Stack key={index} direction="row" sx={{ gap: 1, alignItems: 'center' }}>
            <Box
              sx={{
                width: kind === 'generateStoryboard' ? 26 : 48,
                height: 25,
                bgcolor: 'primary.bg',
                borderRadius: 0.5,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Typography variant="caption">{index + 1}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Line width={`${80 - index * 15}%`} />
            </Box>
          </Stack>
        ))}
      </Stack>
    );
  }
  if (kind === 'generateVideo' || kind === 'generateBanner') {
    const video = kind === 'generateVideo';
    return (video ? [-8, 8] : [-5, 5]).map((rotation, index) => (
      <Box
        key={rotation}
        sx={{
          ...PAPER,
          width: video ? 62 : index === 0 ? 84 : 52,
          height: video ? 108 : index === 0 ? 64 : 94,
          ml: index === 1 ? -2 : 0,
          transform: `rotate(${rotation}deg)`,
          display: 'grid',
          placeItems: 'center',
          background: (theme) =>
            `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.primary.light})`,
        }}
      >
        {video ? (
          <PlayArrowOutlined />
        ) : (
          <Stack sx={{ width: '100%', gap: 1 }}>
            <Line width="90%" />
            <Box sx={{ height: 20, bgcolor: 'primary.main', opacity: 0.15, borderRadius: 0.5 }} />
            <Line width="60%" />
          </Stack>
        )}
      </Box>
    ));
  }
  return <IconBadge icon={iconFor(card.icon, AutoAwesomeOutlined)} size={64} iconSize={32} />;
}
